import React, { useState, useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import { usePageMeta } from '../../../../hooks/usePageMeta';
import { useSearchParams, useNavigate } from 'react-router-dom';
import Header from '../../../../components/2025/Header';
import Footer from '../../../../components/2025/Footer';
import HeroTitle from '../../../../components/2025/HeroTitle';
import EventCard from '../../../../components/2025/EventCard';
import EventFilterNav from '../../../../components/2025/events/EventFilterNav';
import LoadingSpinner from '../../../../components/2025/LoadingSpinner';
import { fetchAndCategorizeEvents, CategorizedEvents } from '../../../../services/eventService';
import { event } from '../../../../types/cms-types';
import { optimizeImage } from '../../../../utils/imageOptimizer';
import Styles from './styles.module.scss';

const convertEventToEventData = (event: event) => ({
  imageUrl: optimizeImage(event.event_image.url, { width: 800, quality: 80 }),
  title: event.event_title,
  time: event.event_time,
  location: event.event_location,
  description: event.event_description,
  labels: [
    ...event.event_category,
    ...event.event_day,
    ...(event.school || []),
    ...(event.faculties || []),
    ...(event.department || []),
  ],
});

const EventsSpecial2025: React.FC = () => {
  const [categorizedEvents, setCategorizedEvents] = useState<CategorizedEvents | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [selectedEvent, setSelectedEvent] = useState<event | null>(null);
  const scrollPositionRef = useRef<number | null>(null);
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();

  usePageMeta({
    title: 'イベント一覧 - 特別企画',
    description:
      'かまた祭2025の特別企画イベント一覧。ビンゴ大会やスタンプラリーなど、特別なイベントをご紹介します。',
  });

  const handleSelectEvent = (event: event) => {
    setSelectedEvent(event);
    const newSearchParams = new URLSearchParams(searchParams);
    newSearchParams.set('event', event.id);
    navigate(`?${newSearchParams.toString()}`, { replace: true });
  };

  const handleCloseModal = () => {
    const savedScrollY = scrollPositionRef.current;

    setSelectedEvent(null);
    const newSearchParams = new URLSearchParams(searchParams);
    newSearchParams.delete('event');
    const paramString = newSearchParams.toString();
    navigate(paramString ? `?${paramString}` : '', { replace: true });

    requestAnimationFrame(() => {
      const body = document.body;
      const html = document.documentElement;
      body.style.position = '';
      body.style.top = '';
      body.style.left = '';
      body.style.right = '';
      body.style.width = '';
      body.style.overflow = '';
      html.style.overflow = '';

      if (savedScrollY !== null) {
        window.scrollTo(0, savedScrollY);
        scrollPositionRef.current = null;
      }
    });
  };

  useEffect(() => {
    const eventId = searchParams.get('event');
    if (eventId && categorizedEvents) {
      const allEvents = Object.values(categorizedEvents).flat();
      const foundEvent = allEvents.find((e) => e.id === eventId);
      if (foundEvent) {
        setSelectedEvent(foundEvent);
      }
    }
  }, [searchParams, categorizedEvents]);

  useEffect(() => {
    if (typeof window === 'undefined') {
      return;
    }

    const body = document.body;
    const html = document.documentElement;

    if (selectedEvent) {
      if (scrollPositionRef.current === null) {
        const scrollY = window.scrollY || html.scrollTop || 0;
        scrollPositionRef.current = scrollY;

        body.style.position = 'fixed';
        body.style.top = `-${scrollY}px`;
        body.style.left = '0';
        body.style.right = '0';
        body.style.width = '100%';
        body.style.overflow = 'hidden';
        html.style.overflow = 'hidden';
      }
    }
  }, [selectedEvent]);

  useEffect(() => {
    const loadEvents = async () => {
      try {
        setIsLoading(true);
        const events = await fetchAndCategorizeEvents();
        setCategorizedEvents(events);
        setError(null);
      } catch (err) {
        console.error('イベントデータの取得に失敗しました:', err);
        setError('イベントデータの読み込みに失敗しました');
      } finally {
        setIsLoading(false);
      }
    };

    loadEvents();
  }, []);

  const specialEvents = categorizedEvents?.['特別企画'] || [];

  return (
    <>
      <Header />
      <main className={Styles.events}>
        <HeroTitle title="EVENT" subtitle="特別企画" />
        <EventFilterNav />

        {isLoading ? (
          <LoadingSpinner />
        ) : error ? (
          <div className={Styles.error}>
            <p>{error}</p>
          </div>
        ) : (
          <section className={Styles.eventSection}>
            <h2 className={Styles.categorySection__title}>特別企画</h2>
            {specialEvents.length > 0 ? (
              <div className={Styles.categorySection__grid}>
                {specialEvents.map((event) => (
                  <div
                    key={event.id}
                    className={Styles.categorySection__card}
                    onClick={() => handleSelectEvent(event)}
                  >
                    <div className={Styles.categorySection__imageWrapper}>
                      <div
                        className={Styles.categorySection__imageBg}
                        style={{
                          backgroundImage: `url(${optimizeImage(event.event_image.url, { width: 800, quality: 75 })})`,
                        }}
                      />
                      <img
                        src={optimizeImage(event.event_image.url, { width: 800, quality: 80 })}
                        alt={event.event_title}
                        className={Styles.categorySection__image}
                      />
                    </div>
                    <div className={Styles.categorySection__content}>
                      <h3 className={Styles.categorySection__eventTitle}>{event.event_title}</h3>
                      <p className={Styles.categorySection__location}>{event.event_location}</p>
                      <div className={Styles.categorySection__footer}>
                        <div className={Styles.categorySection__dateTags}>
                          {event.event_day.map((day, index) => (
                            <span key={index} className={Styles.categorySection__dateTag}>
                              #{day}
                            </span>
                          ))}
                          {event.school?.map((schoolName: string, index: number) => (
                            <span
                              key={`school-${index}`}
                              className={Styles.categorySection__dateTag}
                            >
                              #{schoolName}
                            </span>
                          ))}
                          {event.faculties?.map((faculty: string, index: number) => (
                            <span
                              key={`faculty-${index}`}
                              className={Styles.categorySection__dateTag}
                            >
                              #{faculty}
                            </span>
                          ))}
                          {event.department?.map((dept: string, index: number) => (
                            <span key={`dept-${index}`} className={Styles.categorySection__dateTag}>
                              #{dept}
                            </span>
                          ))}
                        </div>
                        <svg
                          width="25"
                          height="25"
                          viewBox="0 0 25 25"
                          fill="none"
                          xmlns="http://www.w3.org/2000/svg"
                          className={Styles.categorySection__arrow}
                        >
                          <circle
                            cx="12.5"
                            cy="12.5"
                            r="12.5"
                            transform="rotate(90 12.5 12.5)"
                            fill="#0946F1"
                          />
                          <path
                            d="M7.30859 12.5137L16.833 12.6504"
                            stroke="white"
                            strokeLinecap="round"
                          />
                          <path
                            d="M12.5625 8.8457L17.3071 12.6413"
                            stroke="white"
                            strokeLinecap="round"
                          />
                          <path
                            d="M12.5625 16.4441L17.3071 12.6484"
                            stroke="white"
                            strokeLinecap="round"
                          />
                        </svg>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className={Styles.comingSoon}>
                <p className={Styles.comingSoon__text}>Coming Soon...</p>
              </div>
            )}
          </section>
        )}
        <EventFilterNav />
      </main>
      <Footer />
      {selectedEvent &&
        createPortal(
          <div
            className={Styles.modal}
            onClick={handleCloseModal}
            onTouchMove={(e) => e.preventDefault()}
          >
            <div className={Styles.modal__content} onClick={(e) => e.stopPropagation()}>
              <EventCard
                events={[convertEventToEventData(selectedEvent)]}
                onClose={handleCloseModal}
                className={Styles.eventModal}
              />
            </div>
          </div>,
          document.body,
        )}
    </>
  );
};

export default EventsSpecial2025;
